import { graphql } from 'app/generated/gql'

// The two value-stat queries stay separate so one failing analytics resolver
// (a workspace without PMS revenue data) cannot take down the other figure.

export const NEW_PATIENT_VALUE_QUERY = graphql(`
  query ActionCenterNewPatientValue($dateRange: DateRangeInput!, $locationIds: [String!]) {
    newPatientsMetrics(dateRange: $dateRange, locationIds: $locationIds) {
      summary {
        averageProductionPerNewPatientVisit
      }
    }
  }
`)

export const PRODUCTION_VALUE_QUERY = graphql(`
  query ActionCenterProductionValue($dateRange: DateRangeInput!, $locationIds: [String!]) {
    revenueMetrics(dateRange: $dateRange, locationIds: $locationIds) {
      stats {
        avgNetProductionPerVisit
        avgGrossProductionPerVisit
      }
    }
  }
`)

// A Call activity's substance lives on the linked Call record; the activity
// row's own description is boilerplate.
export const CARD_CALL_QUERY = graphql(`
  query ActionCenterCardCall($id: ID!) {
    call(id: $id) {
      id
      answered
      duration
      waveforms
      ai {
        summary
      }
    }
  }
`)

export const CARD_CALL_RECORDING_QUERY = graphql(`
  query ActionCenterCardCallRecording($id: ID!) {
    callRecordingUrl(id: $id)
  }
`)

export const UPDATE_CONTACT_MUTATION = graphql(`
  mutation ActionCenterUpdateContact($id: ID!, $input: UpdateContactInput!) {
    updateContact(id: $id, input: $input) {
      id
    }
  }
`)

// Appends, so concurrent enrolments cannot overwrite each other;
// ALREADY_IN_PIPELINE on a second add is success.
export const ADD_TO_PIPELINE_MUTATION = graphql(`
  mutation ActionCenterAddToPipeline($id: ID!, $pipeline: ContactPipelineInput!) {
    addToPipeline(id: $id, pipeline: $pipeline) {
      id
      pipelines {
        id
        stage
      }
    }
  }
`)

export const PIPELINES_CATALOG_QUERY = graphql(`
  query ActionCenterPipelines {
    pipelines {
      id
      name
      stages {
        name
        color
        order
      }
    }
  }
`)

/** Closes a lead lost via updateContact; the projection makes the Closed Lost milestone. */
export const CLOSE_LOST_MUTATION = graphql(`
  mutation ActionCenterCloseLost($id: ID!, $input: UpdateContactInput!) {
    updateContact(id: $id, input: $input) {
      id
      leadStatus
      closedLostReason
      isQualified
    }
  }
`)

// The follow-up is what clears a missed call: nothing else records a callback.
export const CREATE_FOLLOW_UP_MUTATION = graphql(`
  mutation ActionCenterCreateFollowUp($input: CreateFollowUpInput!) {
    createFollowUp(input: $input) {
      id
      orgId
      workspaceId
      locationId
      contactId
      ownerName
      ownerId
      appointmentDate
      followUpDate
      isConnected
      isScheduled
      description
      createdDate
      updatedDate
    }
  }
`)

// Narrow on purpose: polled every 30s over every message in the workspace.
export const RESPONSE_QUEUE_MESSAGES_QUERY = graphql(`
  query ActionCenterResponseQueueMessages($search: String, $pagination: PaginationInput, $sort: SortInput) {
    messages(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        threadId
        contactId
        from
        locationId
        direction
        text
        read
        createdDate
        sentByUserId
        isAgent
      }
      total
    }
  }
`)

/** A contact's SMS thread, oldest first, for the focus card's conversation face. */
export const CONTACT_MESSAGES_QUERY = graphql(`
  query ActionCenterContactMessages($search: String, $pagination: PaginationInput, $sort: SortInput) {
    messages(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        orgId
        workspaceId
        locationId
        threadId
        from
        to
        callerId
        sentByUserId
        isAgent
        aiWorkflowId
        aiWorkflowName
        isInitialMessage
        contactId
        channel
        subject
        text
        read
        archived
        direction
        createdDate
        updatedDate
        version
      }
      total
      page
    }
  }
`)

// By status, not the contact's denormalized nearestAppointmentDate pointer,
// which carries no status and can name the wrong appointment.
export const LATEST_APPOINTMENT_QUERY = graphql(`
  query ActionCenterLatestAppointment($search: String, $pagination: PaginationInput, $sort: SortInput) {
    appointments(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        date
        status
        type
        locationId
        patientId
        procedureCodes
      }
      total
    }
  }
`)

// The money and dates are backend rollups (TRK-5482); the board never sums.
export const BAND_CONTACTS_QUERY = graphql(`
  query ActionCenterBandContacts($search: String, $pagination: PaginationInput, $sort: SortInput) {
    contacts(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        name
        cellPhone
        patientIds
        locationId
        unbookedTreatmentValue
        lastTreatmentPlanDate
        lastAppointmentDate
        productionLtv
        scheduledProduction
        pipelines {
          id
          stage
        }
        treatmentStatus
      }
      total
    }
  }
`)

/** Every recent appointment for a page of patients, in one request. */
export const BAND_APPOINTMENTS_QUERY = graphql(`
  query ActionCenterBandAppointments($search: String, $pagination: PaginationInput, $sort: SortInput) {
    appointments(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        patientId
        locationId
        status
        pmsStatus
        date
        amount
        type
        description
        procedureCodes
      }
      total
    }
  }
`)

// PENDING only: completed work is history, read off the lifetime rollup.
export const BAND_PROCEDURES_QUERY = graphql(`
  query ActionCenterBandProcedures($search: String, $pagination: PaginationInput, $sort: SortInput) {
    procedures(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        patientId
        locationId
        providerId
        procedureCode
        description
        tooth
        state
        feeAmount
        appointmentIds
        planIds
        procedureCreateDate
        priority
      }
      total
    }
  }
`)

/** The plans those procedures sit on, for whether the practice closed the plan. */
export const BAND_PLANS_QUERY = graphql(`
  query ActionCenterBandPlans($search: String, $pagination: PaginationInput, $sort: SortInput) {
    treatmentPlans(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        patientId
        displayName
        state
        status
        planCreateDate
      }
      total
    }
  }
`)
